/**
 * Avatar Select Popup for Poker Reborn
 *
 * Grid of 43 illustrated avatars for the player to choose from.
 * Follows the Container-direct popup pattern (like BonusPopup).
 *
 * @see src/config/avatars.ts - Avatar ID ↔ asset path mapping
 * @see tasks/active/T32_AVATAR_RESEARCH.md - Avatar system design
 */

import { Assets, Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { Button, GOLD_BUTTON_STYLE } from '@/components/common/Button';
import { avatarService } from '@/services/AvatarService';
import { getAvatarPath, getAllAvatarIds } from '@/config/avatars';

// =============================================================================
// STYLES
// =============================================================================

const STYLES = {
  overlay: {
    color: 0x000000,
    alpha: 0.7,
  },
  popup: {
    width: 420,
    height: 520,
    widthMobile: 340,
    heightMobile: 480,
    fill: 0x1a1a2e,
    stroke: 0x3a3a5e,
    cornerRadius: 15,
    padding: 20,
    paddingMobile: 14,
  },
  title: {
    fill: 0xffd54f,
    fontSize: 22,
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 'bold' as const,
  },
  closeButton: {
    size: 28,
    color: 0x888888,
  },
  grid: {
    cellSize: 52,
    cellSizeMobile: 44,
    gap: 8,
    gapMobile: 6,
    cols: 7,
    colsMobile: 6,
  },
  cell: {
    fill: 0x2a2a4e,
    stroke: 0x4a4a6e,
    strokeWidth: 2,
    selectedStroke: 0xffd700,
    selectedStrokeWidth: 3,
    hoverStroke: 0x888888,
  },
  confirm: {
    width: 200,
    height: 44,
    fontSize: 16,
  },
  breakpoints: {
    mobile: 480,
  },
};

// =============================================================================
// AVATAR SELECT POPUP
// =============================================================================

export class AvatarSelectPopup extends Container {
  private overlay: Graphics;
  private panel: Container;
  private popupWidth: number;
  private popupHeight: number;
  private padding: number;
  private screenWidth: number;
  private screenHeight: number;
  private isMobile: boolean;

  // Grid
  private gridContainer: Container;
  private cellHighlights: Map<number, Graphics> = new Map();

  // State
  private selectedAvatarId: number | null;
  private confirmButton: Button;

  /** Called when popup is dismissed (close or confirm) */
  onDismiss: (() => void) | null = null;

  constructor(
    screenWidth: number,
    screenHeight: number,
    currentAvatarId: number | null,
  ) {
    super();

    this.screenWidth = screenWidth;
    this.screenHeight = screenHeight;
    this.selectedAvatarId = currentAvatarId;

    this.isMobile = screenWidth < STYLES.breakpoints.mobile;
    this.popupWidth = this.isMobile ? STYLES.popup.widthMobile : STYLES.popup.width;
    this.popupHeight = this.isMobile ? STYLES.popup.heightMobile : STYLES.popup.height;
    this.padding = this.isMobile ? STYLES.popup.paddingMobile : STYLES.popup.padding;

    // Full-screen overlay (tap to dismiss)
    this.overlay = new Graphics();
    this.overlay.rect(0, 0, screenWidth, screenHeight);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });
    this.overlay.eventMode = 'static';
    this.overlay.cursor = 'pointer';
    this.overlay.on('pointertap', () => { this.handleDismiss(); });
    this.addChild(this.overlay);

    // Centered panel
    this.panel = new Container();
    this.panel.x = (screenWidth - this.popupWidth) / 2;
    this.panel.y = (screenHeight - this.popupHeight) / 2;
    this.addChild(this.panel);

    // Panel background
    const panelBg = new Graphics();
    panelBg.roundRect(0, 0, this.popupWidth, this.popupHeight, STYLES.popup.cornerRadius);
    panelBg.fill(STYLES.popup.fill);
    panelBg.setStrokeStyle({ width: 2, color: STYLES.popup.stroke });
    panelBg.stroke();
    panelBg.eventMode = 'static';
    panelBg.on('pointerdown', (e) => { e.stopPropagation(); });
    this.panel.addChild(panelBg);

    // Title
    const titleText = new Text({
      text: 'Choose Your Avatar',
      style: STYLES.title,
    });
    titleText.x = this.padding;
    titleText.y = this.padding;
    this.panel.addChild(titleText);

    // Close button (X)
    const closeBtn = this.createCloseButton();
    closeBtn.x = this.popupWidth - this.padding - STYLES.closeButton.size;
    closeBtn.y = this.padding;
    this.panel.addChild(closeBtn);

    // Grid container
    this.gridContainer = new Container();
    this.gridContainer.x = this.padding;
    this.gridContainer.y = this.padding + 50;
    this.panel.addChild(this.gridContainer);

    // Confirm button
    this.confirmButton = new Button({
      label: 'Confirm',
      width: STYLES.confirm.width,
      height: STYLES.confirm.height,
      fontSize: STYLES.confirm.fontSize,
      ...GOLD_BUTTON_STYLE,
    });
    this.confirmButton.x = (this.popupWidth - STYLES.confirm.width) / 2;
    this.confirmButton.y = this.popupHeight - this.padding - STYLES.confirm.height;
    this.confirmButton.onClick = () => { this.handleConfirm(); };
    this.confirmButton.disabled = currentAvatarId === null;
    this.panel.addChild(this.confirmButton);

    // Build grid and load textures
    this.createGrid();
    this.loadAllAvatarTextures();
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /** Resize overlay and re-center panel */
  resize(width: number, height: number): void {
    this.screenWidth = width;
    this.screenHeight = height;

    this.overlay.clear();
    this.overlay.rect(0, 0, width, height);
    this.overlay.fill({ color: STYLES.overlay.color, alpha: STYLES.overlay.alpha });

    this.panel.x = (width - this.popupWidth) / 2;
    this.panel.y = (height - this.popupHeight) / 2;
  }

  // ===========================================================================
  // PRIVATE — GRID CONSTRUCTION
  // ===========================================================================

  private createGrid(): void {
    const cellSize = this.isMobile ? STYLES.grid.cellSizeMobile : STYLES.grid.cellSize;
    const gap = this.isMobile ? STYLES.grid.gapMobile : STYLES.grid.gap;
    const cols = this.isMobile ? STYLES.grid.colsMobile : STYLES.grid.cols;
    const avatarIds = getAllAvatarIds();

    for (let i = 0; i < avatarIds.length; i++) {
      const avatarId = avatarIds[i];
      if (avatarId === undefined) continue;
      const col = i % cols;
      const row = Math.floor(i / cols);

      const cell = this.createCell(avatarId, cellSize);
      cell.x = col * (cellSize + gap);
      cell.y = row * (cellSize + gap);
      this.gridContainer.addChild(cell);
    }

    // Center the grid horizontally in the popup
    const totalGridWidth = cols * cellSize + (cols - 1) * gap;
    this.gridContainer.x = (this.popupWidth - totalGridWidth) / 2;
  }

  private createCell(avatarId: number, cellSize: number): Container {
    const cell = new Container();
    const radius = cellSize / 2;

    // Background circle
    const bg = new Graphics();
    bg.circle(radius, radius, radius);
    bg.fill(STYLES.cell.fill);
    cell.addChild(bg);

    // Selection highlight ring (hidden by default, shown when selected)
    const highlight = new Graphics();
    highlight.label = 'highlight';
    this.drawCellHighlight(highlight, radius, avatarId === this.selectedAvatarId);
    cell.addChild(highlight);
    this.cellHighlights.set(avatarId, highlight);

    // Placeholder (will be replaced when texture loads)
    const placeholder = new Graphics();
    placeholder.circle(radius, radius, radius - 3);
    placeholder.fill(0x3a3a5e);
    placeholder.label = 'placeholder';
    cell.addChild(placeholder);

    // Make interactive
    cell.eventMode = 'static';
    cell.cursor = 'pointer';
    cell.on('pointerdown', () => { this.selectAvatar(avatarId); });

    // Store avatarId on the cell for texture loading
    cell.label = `cell_${String(avatarId)}`;

    return cell;
  }

  private drawCellHighlight(highlight: Graphics, radius: number, selected: boolean): void {
    highlight.clear();
    if (selected) {
      highlight.circle(radius, radius, radius);
      highlight.setStrokeStyle({ width: STYLES.cell.selectedStrokeWidth, color: STYLES.cell.selectedStroke });
      highlight.stroke();
    } else {
      highlight.circle(radius, radius, radius);
      highlight.setStrokeStyle({ width: STYLES.cell.strokeWidth, color: STYLES.cell.stroke });
      highlight.stroke();
    }
  }

  // ===========================================================================
  // PRIVATE — TEXTURE LOADING
  // ===========================================================================

  private loadAllAvatarTextures(): void {
    const cellSize = this.isMobile ? STYLES.grid.cellSizeMobile : STYLES.grid.cellSize;
    const avatarIds = getAllAvatarIds();

    // Load all avatar textures in parallel
    const paths = avatarIds.map((id) => getAvatarPath(id));
    void Assets.load(paths).then(() => {
      if (this.destroyed) return;

      // Now create sprites for each cell
      for (const avatarId of avatarIds) {
        this.addSpriteToCell(avatarId, cellSize);
      }
    });
  }

  private addSpriteToCell(avatarId: number, cellSize: number): void {
    const cellLabel = `cell_${String(avatarId)}`;
    const cell = this.gridContainer.children.find((c) => c.label === cellLabel);
    if (!cell) return;

    const path = getAvatarPath(avatarId);
    const texture = Assets.get<Texture>(path);

    const radius = cellSize / 2;

    // Create sprite
    const sprite = new Sprite(texture);
    sprite.width = cellSize - 4; // Slightly smaller than cell for border breathing room
    sprite.height = cellSize - 4;
    sprite.anchor.set(0.5);
    sprite.x = radius;
    sprite.y = radius;
    sprite.label = 'avatarSprite';

    // Circular mask
    const mask = new Graphics();
    mask.circle(radius, radius, radius - 2);
    mask.fill(0xffffff);
    cell.addChild(mask);

    sprite.mask = mask;
    cell.addChild(sprite);

    // Remove placeholder
    const placeholder = (cell as Container).children.find((c) => c.label === 'placeholder');
    if (placeholder) {
      (cell as Container).removeChild(placeholder);
      placeholder.destroy();
    }
  }

  // ===========================================================================
  // PRIVATE — SELECTION
  // ===========================================================================

  private selectAvatar(avatarId: number): void {
    const cellSize = this.isMobile ? STYLES.grid.cellSizeMobile : STYLES.grid.cellSize;
    const radius = cellSize / 2;

    // Deselect previous
    if (this.selectedAvatarId !== null) {
      const prevHighlight = this.cellHighlights.get(this.selectedAvatarId);
      if (prevHighlight) {
        this.drawCellHighlight(prevHighlight, radius, false);
      }
    }

    // Select new
    this.selectedAvatarId = avatarId;
    const highlight = this.cellHighlights.get(avatarId);
    if (highlight) {
      this.drawCellHighlight(highlight, radius, true);
    }

    // Enable confirm button
    this.confirmButton.disabled = false;
  }

  // ===========================================================================
  // PRIVATE — ACTIONS
  // ===========================================================================

  private handleConfirm(): void {
    if (this.selectedAvatarId !== null) {
      avatarService.setAvatar(this.selectedAvatarId);
    }
    this.close();
  }

  private handleDismiss(): void {
    this.close();
  }

  private close(): void {
    this.onDismiss?.();
    this.destroy({ children: true });
  }

  // ===========================================================================
  // PRIVATE — UI HELPERS
  // ===========================================================================

  private createCloseButton(): Container {
    const container = new Container();
    const size = STYLES.closeButton.size;

    const bg = new Graphics();
    bg.circle(size / 2, size / 2, size / 2);
    bg.fill({ color: 0x000000, alpha: 0.3 });
    container.addChild(bg);

    const xText = new Text({
      text: '\u2715',
      style: {
        fill: STYLES.closeButton.color,
        fontSize: 16,
        fontFamily: 'Roboto, Arial, sans-serif',
      },
    });
    xText.anchor.set(0.5);
    xText.x = size / 2;
    xText.y = size / 2;
    container.addChild(xText);

    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.on('pointertap', () => { this.close(); });

    return container;
  }
}
